/**
 * Wire types for the content service (`agentic-content-generation-service`).
 *
 * The service serialises every schema through Pydantic's `to_camel` alias
 * generator, so the wire is already camelCase and UI-ready — no mapper layer,
 * same call as `workflow-tracking`.
 *
 * Enum-valued fields are typed as their union where the service validates them
 * on the way IN (requests), and as `string` where they merely come back out of
 * ClickHouse. That mirrors the service's own rule — "requests validate strictly,
 * responses read leniently" — so a row written by a newer worker mid-deploy
 * renders as an unknown value instead of blowing up the whole page.
 */

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

/** Every non-paginated response. `data` is null on the error paths. */
export interface ContentResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
}

/**
 * Pagination meta. The service builds this two different ways and the fields
 * that are meaningful differ:
 * - keyset endpoints (runs, batches, content, failures) set only `nextCursor` +
 *   `hasNextPage`; `total` and `totalPages` are always 0, so a page COUNT
 *   cannot be shown for them.
 * - page/size endpoints (gap analysis, later phase) set a real `total`.
 */
export interface ContentPaginationMeta {
  total: number;
  page: number;
  size: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
  nextCursor: string | null;
}

export interface ContentPaginatedResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  pagination: ContentPaginationMeta;
}

/** One keyset page, unwrapped for the hooks that accumulate pages. */
export interface ContentPage<T> {
  items: T[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Enums — mirrored from `content_core.models.enums`
// ---------------------------------------------------------------------------

export const RUN_STATUSES = [
  'PENDING',
  'PLANNING',
  'GENERATING',
  'PAUSED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** The three statuses after which a run never moves again. */
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = ['COMPLETED', 'FAILED', 'CANCELLED'];

export const BATCH_STATUSES = [
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
  'CANCELLED',
] as const;
export type BatchStatus = (typeof BATCH_STATUSES)[number];

/** The basic five a run off the field registry writes (no flows, or a channel other than GMC / Meta). */
export const CONTENT_FIELDS = ['TITLE', 'DESCRIPTION', 'BULLETS', 'KEYWORDS', 'CATEGORY'] as const;
export type ContentField = (typeof CONTENT_FIELDS)[number];

export const CONTENT_PROVIDERS = ['GOOGLE_MERCHANT_CENTER', 'AMAZON', 'META_COMMERCE'] as const;
export type ContentProvider = (typeof CONTENT_PROVIDERS)[number];

export const RUN_TRIGGERS = ['MANUAL', 'API', 'SCHEDULE', 'BACKFILL', 'RETRY'] as const;
export type RunTrigger = (typeof RUN_TRIGGERS)[number];

/** Where a run came in (backend `RunOrigin`); decided by the route and token, and filterable on `GET /runs`. */
export const RUN_ORIGINS = ['CONTENT_CONSOLE', 'PLATFORM_SERVICE', 'SCHEDULE', 'OPERATOR'] as const;
export type RunOrigin = (typeof RUN_ORIGINS)[number];

export const FLOWS = [
  'CORE_ATTRIBUTION',
  'PRODUCT_TYPE_DETECTION',
  'SEARCH_DESCRIPTORS',
  'PHRASE_DESCRIPTORS',
  'CONTENT_ENRICHMENT',
  'CONTENT_DETERMINISTIC_SCORING',
  'CONTENT_LLM_SCORING',
  'CONTENT_GAP_ANALYSIS',
] as const;
export type Flow = (typeof FLOWS)[number];

/** The control signals `POST /runs/{id}/{action}` accepts. */
export type RunAction = 'pause' | 'resume' | 'cancel';

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

/** The stored cohort block a run was started with. */
export interface SourceConfigRead {
  productIds: string[];
  brands: string[];
  categories: string[];
  updatedAfter: string | null;
  limit: number | null;
  /** Empty for every run that used the deployment's configured source table. */
  sourceTableRef: string;
}

/** The durable ClickHouse run row. */
export interface GenerationRunRead {
  runId: string;
  /** QC_CHECK · CATALOG_RUN · JOB, read by the service from the run id. Absent on older services. */
  kind?: 'QC_CHECK' | 'CATALOG_RUN' | 'JOB';
  subsidiaryId: string;
  workflowId: string;
  status: string;
  stage: string;
  trigger: string;
  requestedBy: string;
  /** USER · SERVICE · SCHEDULE · SYSTEM · UNKNOWN. Empty on pre-column rows. */
  actorKind: string;
  /**
   * A `RunOrigin` value: where the run came in. Always sent; empty for a run
   * created before origins were recorded.
   */
  origin: RunOrigin | '';
  /** The platform service's client id that started it; empty unless `PLATFORM_SERVICE`. */
  callerService: string;
  /** Empty means the run predates the two-namespace split. */
  namespace: string;
  fields: string[];
  /**
   * The AI steps the run was asked to run (e.g. product-type detection, scoring).
   * Always sent; empty means the default content run (or a run created before flows were recorded).
   */
  flows: string[];
  /**
   * The detected verticals the run was asked to enrich (`Vertical` values, request order). Empty
   * means every vertical; optional because a service build before the column omits it.
   */
  enrichVerticals?: string[];
  model: string;
  promptVersion: string;
  language: string;
  batchSize: number;
  productFilter: SourceConfigRead;
  totalProducts: number;
  totalBatches: number;
  completedBatches: number;
  failedBatches: number;
  generatedItems: number;
  failedItems: number;
  errorMessage: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** Hidden from the default run list (archived); optional for service builds before archiving. */
  archived?: boolean;
  archivedAt?: string | null;
  /** The archiving user's email (else their subject). */
  archivedBy?: string | null;
}

/** The workflow's own view, queried live from Temporal. */
export interface RunProgressRead {
  runId: string;
  subsidiaryId: string;
  status: string;
  stage: string;
  totalProducts: number;
  totalBatches: number;
  completedBatches: number;
  failedBatches: number;
  generatedItems: number;
  failedItems: number;
  paused: boolean;
  cancelRequested: boolean;
  errorMessage: string;
  startedAt: string | null;
  updatedAt: string | null;
  percentComplete: number;
  /** Where the counters came from: the live workflow, the batch rows, or the run row. */
  progressSource?: 'LIVE' | 'BATCH_ROWS' | 'RUN_ROW';
  /** Products the run is finished with (exact from finished batches); null before any. */
  productsDone?: number | null;
  /** `productsDone` per minute since the start; null before the first batch finishes. */
  throughputProductsPerMinute?: number | null;
  /** Seconds left at that rate; null once finished or while throughput is unknown. */
  etaSeconds?: number | null;
}

/**
 * Row + live progress. `live` is false once the run has finished OR when
 * Temporal could not be reached — the row is still returned either way, so the
 * UI degrades to stored counters rather than showing nothing.
 */
export interface GenerationRunDetail {
  run: GenerationRunRead;
  progress: RunProgressRead | null;
  live: boolean;
}

/** What a pause/resume/cancel answers with. The status is pre-transition. */
export interface RunActionRead {
  runId: string;
  action: string;
  workflowId: string;
  status: string;
}

export interface BatchRead {
  runId: string;
  batchIndex: number;
  batchWorkflowId: string;
  status: string;
  cursorAfter: string;
  productCount: number;
  generatedCount: number;
  failedCount: number;
  attempt: number;
  errorMessage: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string | null;
}

export interface GeneratedContentRead {
  productId: string;
  field: string;
  content: string;
  contentHash: string;
  model: string;
  promptVersion: string;
  language: string;
  batchIndex: number;
  promptTokens: number;
  completionTokens: number;
  latencyMs: number;
  generatedAt: string | null;
}

export interface ItemFailureRead {
  productId: string;
  failedAt: string | null;
  field: string;
  reason: string;
  message: string;
  attempt: number;
  batchIndex: number;
}

export interface TokenUsageRead {
  promptTokens: number;
  completionTokens: number;
  avgLatencyMs: number;
}

export interface RunMetricsRead {
  runId: string;
  metrics: Record<string, number>;
  batchesByStatus: Record<string, number>;
  /** Failed ATTEMPTS per reason (retries counted again). */
  failuresByReason: Record<string, number>;
  tokenUsage: TokenUsageRead;
  /** R6 (appended, may be absent/null): distinct PRODUCTS per failure reason. */
  failedProductsByReason?: Record<string, number> | null;
  /** R6: distinct products with any failure — not the sum of the map. */
  failedProducts?: number | null;
  /** R4: summed stored per-product cost; null when nothing carries one (every run today). */
  costUsd?: number | null;
  /** B7 (appended, may be absent): why `costUsd` is null — `NO_TOKENS_RECORDED` (no content leaf
   *  carries tokens) or `PRICES_NOT_REAL` (no model that wrote them has a real price yet). */
  costUnavailableReason?: 'PRICES_NOT_REAL' | 'NO_TOKENS_RECORDED' | (string & {}) | null;
  /** R4: how many products `costUsd` sums. */
  costedProducts?: number | null;
  /** R4: the model most leaves were written with, else the run's configured model. */
  model?: string | null;
  /** R4: every model the run's leaves were written with, most-used first. */
  models?: string[] | null;
}

// ---------------------------------------------------------------------------
// Start-run request
// ---------------------------------------------------------------------------

/** The cohort selector on `POST /runs`. Every field is optional. */
export interface ProductFilterPayload {
  productIds?: string[];
  /** Restrict to these brands (exact values from the catalog). */
  brands?: string[];
  /** Restrict to these categories (exact values from the catalog). */
  categories?: string[];
  /** ISO 8601 instant — only products whose source row changed after it. */
  updatedAfter?: string;
  includePreviouslyGenerated?: boolean;
  limit?: number;
}

/**
 * One prompt pinned to a run (`PromptSelection` on the service). The flow must
 * also be named in `flows` and must not be FREE. The service keeps ONE active
 * prompt per (profile, vertical, platform, flow) and refuses DISABLED ones, so
 * a pin can only restate the default — the Start Run dialog never sends one.
 */
export interface PromptSelection {
  flow: string;
  /** Required, min length 1. An unknown or DISABLED version is a 422. */
  version: string;
  /** Required whenever the flow has per-vertical bodies; empty otherwise. */
  vertical?: string;
  /** Echoed for readability only — never matched on. */
  name?: string;
}

/**
 * `POST /runs` body. `stagePlan`, `callback` and `extraConfig` are deliberately
 * left out of the UI — `flows` is the caller-facing way to say what a run does,
 * and `stagePlan` is the escape hatch for replaying a historical plan.
 */
export interface GenerationRunCreate {
  /** Supply to make the call idempotent — a repeat joins the in-flight run. */
  runId?: string;
  /**
   * `string[]`, not `ContentField[]`: a flows run on GOOGLE_MERCHANT_CENTER / META_COMMERCE takes
   * the registry's names (`GET /enrichments/fields` `uiName`, or its codes); any other run takes
   * one of `CONTENT_FIELDS`. The service validates either (422 naming an unknown one).
   */
  fields?: string[];
  productFilter?: ProductFilterPayload;
  batchSize?: number;
  maxBatches?: number;
  model?: string;
  promptVersion?: string;
  /** Server enforces 2–16 characters. */
  language?: string;
  dryRun?: boolean;
  trigger?: RunTrigger;
  platform?: ContentProvider;
  /**
   * Mutually exclusive with `stagePlan`, which this form does not send.
   *
   * `string[]`, not `Flow[]`, on purpose: the selectable flows come from
   * `GET /model-configuration/flows` at runtime, so the closed union this
   * client ships is a snapshot, not the authority. Narrowing a server value to
   * it required a cast that would quietly lie the moment the service added a
   * flow. The service validates the value as its own `Flow` enum anyway.
   */
  flows?: string[];
  /**
   * Which prompt version each flow runs. A flow omitted here takes its ACTIVE
   * prompt for the run's workspace (custom, else default) and is pinned to it
   * anyway, so the run records what it used.
   */
  prompts?: PromptSelection[];
  /**
   * Re-extract core attributes even where the reuse cache says they are
   * current. Expensive — it re-pays for every product in the run.
   */
  forceExtract?: boolean;
  /**
   * The detected verticals this run enriches (max 20). Omitted or empty: every vertical. A product
   * of another vertical is still detected and scored but not written (`SKIPPED_NOT_SELECTED`);
   * `NO_MATCH` selects products detection placed in no vertical. Needs `CONTENT_ENRICHMENT`.
   */
  enrichVerticals?: string[];
}

// ---------------------------------------------------------------------------
// Supporting vocabulary
// ---------------------------------------------------------------------------

/** Query filters for the runs list. */
export interface RunListFilters {
  statuses?: RunStatus[];
  /** Where the runs came in; sent as a repeatable `origin` param. Omitted means every origin. */
  origins?: RunOrigin[];
  /** Archived runs: hidden (default), only them, or both. */
  archived?: RunArchiveFilter;
  /** QC_CHECK / CATALOG_RUN / JOB, by the run id the console minted; repeatable. */
  kinds?: ('QC_CHECK' | 'CATALOG_RUN' | 'JOB')[];
  /** Runs that wrote products for these channels; repeatable. */
  platforms?: string[];
  /** ISO 8601: runs created at or after it. */
  createdAfter?: string;
  limit?: number;
}

/** `GET /runs?archived=` — EXCLUDE (default) hides archived runs. */
export type RunArchiveFilter = 'EXCLUDE' | 'ONLY' | 'ALL';

/** What an archive / unarchive did to one run. */
export type RunArchiveOutcome =
  | 'ARCHIVED'
  | 'UNARCHIVED'
  | 'UNCHANGED'
  | 'NOT_FOUND'
  | 'NOT_FINISHED';

/** `POST /runs/{id}/archive|unarchive` answer, and one row of the bulk answer. */
export interface RunArchiveRead {
  runId: string;
  /** The archive state after the request. */
  archived: boolean;
  outcome: RunArchiveOutcome;
  archivedAt: string | null;
  archivedBy: string | null;
  runStatus: string | null;
}

/** `POST /runs/archive` answer: one result per id sent, in request order, plus counts. */
export interface RunBulkArchiveRead {
  results: RunArchiveRead[];
  changed: number;
  unchanged: number;
  refused: number;
}

/** Query filters for a run's generated content. */
export interface RunContentFilters {
  fields?: ContentField[];
  productId?: string;
  limit?: number;
}

/** Query filters for a run's batches. */
export interface RunBatchFilters {
  statuses?: BatchStatus[];
  limit?: number;
}

/** Readiness payload — one entry per external dependency. */
export interface ContentDependencyStatus {
  name: string;
  /** `ok` · `down` · `disabled`. */
  status: string;
  detail: string;
}

export interface ContentHealthRead {
  status: string;
  service: string;
  version: string;
  environment: string;
  timestamp: string;
  checks: ContentDependencyStatus[];
}
